import { useEffect, useRef } from "react";
import { Spinner } from "@repo/ui";
import { useBrandsQuery } from "~/apis/use-brands-query";
import type { Task, Item } from "~/types/api";
import { useItemSync } from "./hooks/use-item-sync";
import { useOpenTaskWindow } from "./tab-windows/hooks/use-open-task-window";
import {
  useTabWindowsActions,
  useTabWindowsState,
} from "./tab-windows/hooks/use-tab-windows-context";
import { useWindowQueryParams } from "./tab-windows/hooks/use-window-query-params";
import {
  ALL_TASKS_BRAND_PREFIX,
  ALL_TASKS_PREFIX,
  getTaskIdFromWindow,
  TASKS_PREFIX,
} from "./tab-windows/utils/window-helpers";
import type { ExplorerItem } from "./task.types";
import { MinimizedWindowTray } from "./ui/minimized/task-explorer-min-win-tray";
import { WindowRenderer } from "./ui/window-renderer";

type Props = {
  tasks?: Task[];
};

const LoadingView = () => {
  return (
    <div className="flex w-full items-center justify-center">
      <div className="text-center">
        <Spinner className="mr-2" />
        <p className="text-gray-600">Loading pinned windows...</p>
      </div>
    </div>
  );
};

export const TasksPageContent = ({ tasks }: Props) => {
  const {
    activeWindows,
    activePanelWindowId,
    detailViewItem,
    isLoadingDetailView,
    isInitializing,
  } = useTabWindowsState<ExplorerItem>();
  const { closeWindow, updateWindowState, closeDetailView } = useTabWindowsActions<ExplorerItem>();

  useItemSync();

  const { openTaskWindow, openAllTasksWindow, refreshTasksWindow } = useOpenTaskWindow();
  const { data: brands } = useBrandsQuery();

  const activeWindowsRef = useRef(activeWindows);

  useEffect(() => {
    activeWindowsRef.current = activeWindows;
  }, [activeWindows]);

  useWindowQueryParams({
    activeWindows,
    tasks,
    brands,
    openTaskWindow,
    openAllTasksWindow,
  });

  useEffect(() => {
    if (!tasks) return;

    const activeTasks = tasks.filter((t) => t.active);

    activeWindowsRef.current.forEach((window) => {
      if (window.id.startsWith(TASKS_PREFIX)) {
        const taskId = getTaskIdFromWindow(window.id);
        const task = tasks.find((t) => t.id === taskId);

        if (!task || !task.active) {
          closeWindow(window.id);
        } else if (task.name !== window.state.title) {
          updateWindowState(window.id, { ...window.state, title: task.name });
        }
        return;
      }

      const isAllTasks = window.id === ALL_TASKS_PREFIX;
      const brandId = getTaskIdFromWindow(window.id, ALL_TASKS_BRAND_PREFIX);

      // Only all-tasks and brand all-tasks windows track the active task set
      if (!isAllTasks && brandId === null) return;

      const desiredTasks =
        brandId !== null ? activeTasks.filter((t) => t.brandId === brandId) : activeTasks;
      const currentTaskIds = new Set(window.state.taskIds ?? []);
      const desiredTaskIds = new Set(desiredTasks.map((task) => task.id));

      const changed =
        currentTaskIds.size !== desiredTaskIds.size ||
        [...desiredTaskIds].some((id) => !currentTaskIds.has(id));

      if (changed) {
        refreshTasksWindow(window.id, desiredTasks, window.state.title, window.state.subtitle);
      }
    });
  }, [tasks, closeWindow, updateWindowState, refreshTasksWindow]);

  return (
    <>
      <div className="flex h-screen overflow-auto">
        {isInitializing ? (
          <LoadingView />
        ) : (
          activeWindows.map((window) => {
            const parentTasks: Task[] | undefined = tasks?.filter((t) =>
              window.state.taskIds?.includes(t.id)
            );
            return (
              <WindowRenderer
                key={window.id}
                window={window}
                tasks={parentTasks}
                isLoadingDetailView={isLoadingDetailView}
                showDetailView={activePanelWindowId === window.id}
                detailViewItem={detailViewItem as Item[]}
                onCloseDetailView={closeDetailView}
              />
            );
          })
        )}
      </div>
      <MinimizedWindowTray />
    </>
  );
};
